/**
 * Underpayment Recovery — /underpayment
 *
 * Admin + Super User only. Reads from the existing appt_status / attendance
 * / vault_contract_rates data (no new imports). Four surfaces:
 *   1. Summary bar — total open $, action-today, scheduled, critically aged
 *   2. Today's Action List — Action-needed items, $ desc
 *   3. Full Tracker — every open row, sortable + filterable, click to edit
 *   4. Terminal History — archived Resolved / Written Off items (read-only)
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { fmtDate, fmtDateTime, ctDateKey, ctWeekdayIso } from '../lib/date';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertTriangle, Clock, CheckCircle2, DollarSign, Users, RefreshCw,
  Search, X, ArrowUpDown, Archive, Zap, Settings, Plus, Trash2, ChevronUp, ChevronDown,
  TrendingDown, Crown,
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import InfoTooltip from '../components/InfoTooltip';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';

const TABS = [
  { key: 'action',   label: 'Today\'s Action List', icon: Zap },
  { key: 'tracker',  label: 'Full Tracker',         icon: ArrowUpDown },
  { key: 'terminal', label: 'Terminal History',     icon: Archive },
];

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const int = (v) => (Number(v) || 0).toLocaleString();

// ── Date formatting ──
// The whole module uses MM-DD-YYYY for display; storage stays ISO. Handles
// ISO strings ('2025-11-06', '2025-11-06T14:22:00Z'), Date objects, and
// dashes-vs-slashes ambiguity — anything unparseable is returned untouched.
function fmtDate_UNUSED_LOCAL_REMOVED() { /* removed iter 255 — use lib/date.fmtDate */ }
function fmtDateTime_UNUSED_LOCAL_REMOVED() { /* removed iter 255 — use lib/date.fmtDateTime */ }

export default function Underpayment() {
  const [tab, setTab] = useState('action');
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  // Shared DOS window — narrows Summary Bar, Action List, Full Tracker.
  // Empty strings = "no bound" so the backend returns everything.
  const [dosFrom, setDosFrom] = useState('');
  const [dosTo, setDosTo]     = useState('');
  // Focused work-queue filters — cascade to Summary Bar counts/totals,
  // Payer bars, Top clients, Recovery Trend snapshot's per-payer view,
  // Action List, and Full Tracker so every surface stays in sync.
  const [payerGroupsFilter, setPayerGroupsFilter] = useState([]); // array of group names
  const [minUnderpaid, setMinUnderpaid] = useState('');           // string; empty = no floor
  // Iter 261 — Top-summary tile filter. Values: 'all' | 'reviewed'
  // | 'scheduled' | 'critical'. Clicking a tile sets this and forces
  // the Tracker tab; the FullTracker renders an active-filter badge
  // + Clear button so the user always knows what they're looking at.
  const [presetFilter, setPresetFilter] = useState('all');

  const loadSummary = useCallback(async () => {
    try {
      const params = {};
      if (dosFrom) params.dos_from = dosFrom;
      if (dosTo)   params.dos_to   = dosTo;
      if (payerGroupsFilter.length) params.payer_groups = payerGroupsFilter.join(',');
      if (minUnderpaid && parseFloat(minUnderpaid) > 0) params.min_underpaid = parseFloat(minUnderpaid);
      const { data } = await api.get('/underpayment/summary', { params });
      setSummary(data);
    } catch (e) { /* silent */ }
  }, [dosFrom, dosTo, payerGroupsFilter, minUnderpaid]);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/underpayment/config');
      setConfig(data);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadSummary(); loadConfig(); }, [loadSummary, loadConfig]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post('/underpayment/refresh');
      toast.success(
        `Refresh done · ${data.tracker_size} open · ${data.new_rows} new · ${data.auto_resolved} auto-resolved · ${data.archived_this_run} archived`
      );
      loadSummary();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Refresh failed'); }
    finally { setRefreshing(false); }
  };

  return (
    <div className="space-y-6 pb-24" data-testid="underpayment-page">
      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-br from-[#685B72] via-[#7A6B8A] to-[#839E97] px-8 py-7 text-white">
        <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80 flex items-center gap-1.5">
          <DollarSign size={12} /> Underpayment Recovery
          <InfoTooltip testId="up-help" text="Flags every attended (Show) appointment where insurance + client payment together fell more than $2 short of the contracted rate. Terminal History rows are never re-flagged. Refresh runs after every weekly import automatically." />
        </div>
        <h1 className="font-serif text-4xl mt-1">Every dollar has a name — find it, chase it, close it.</h1>
        <p className="text-[13px] mt-2 opacity-90 max-w-3xl">
          Pull-based: reads directly from the weekly Appointment Status + Attendance uploads. Admin-owned fields (status, notes, follow-up dates) are never touched by a refresh. Marking an item <b>Resolved</b> or <b>Written Off</b> archives it permanently.
        </p>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Button
            onClick={refresh} disabled={refreshing}
            className="bg-white/95 text-[#4A3B5F] hover:bg-white h-8 rounded-full text-[12px] font-semibold"
            data-testid="up-refresh-btn"
          >
            <RefreshCw size={13} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh now'}
          </Button>
          <Button
            onClick={() => setShowConfig(true)}
            variant="outline"
            className="bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white h-8 rounded-full text-[12px] font-semibold"
            data-testid="up-config-btn"
          >
            <Settings size={13} className="mr-1.5" /> Settings
          </Button>
          {summary?.last_refresh && (
            <div className="text-[11px] opacity-80" data-testid="up-last-refresh">
              Last refresh: {fmtDateTime(summary.last_refresh.finished_at || summary.last_refresh.at)} · {summary.last_refresh.actor}
            </div>
          )}
        </div>
      </div>

      {showConfig && (
        <ConfigDrawer
          config={config}
          onClose={() => setShowConfig(false)}
          onSaved={(next) => { setConfig(next); setShowConfig(false); loadSummary(); }}
        />
      )}

      {/* Date-of-Service window — narrows Summary Bar, Action List, Full Tracker. */}
      <DosRangeBar
        dosFrom={dosFrom} dosTo={dosTo}
        onChange={(a, b) => { setDosFrom(a); setDosTo(b); }}
      />

      {/* Focused-queue filters — payer(s) + min underpaid $. Cascade to
          every count/total/chart below so admin can carve a precise
          slice (e.g. Cigna claims ≥ $25) and work it in one sitting. */}
      <FocusedQueueBar
        payerGroups={payerGroupsFilter}
        onChangePayerGroups={setPayerGroupsFilter}
        minUnderpaid={minUnderpaid}
        onChangeMinUnderpaid={setMinUnderpaid}
        summaryPayerBuckets={summary?.by_payer_group || []}
      />

      {/* Summary bar — iter 261. Every tile is a filter shortcut into
          the Full Tracker; the `presetFilter` state below carries the
          selection down to <FullTracker>, which also renders the active
          filter badge + a Clear button. */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="up-summary-bar">
        <KpiTile
          icon={AlertTriangle} tone="red"
          label="Total Underpaid Appointments" value={int(summary?.open_count)}
          sub="All open" testId="kpi-total-underpaid"
          onClick={() => { setPresetFilter('all'); setTab('tracker'); }}
        />
        <KpiTile
          icon={AlertTriangle} tone="red"
          label="Total $ Amount Underpaid" value={money(summary?.open_dollars)}
          sub={`${int(summary?.open_count)} appt${int(summary?.open_count) === 1 ? '' : 's'}`}
          testId="kpi-total-dollars"
          onClick={() => { setPresetFilter('all'); setTab('tracker'); }}
        />
        <KpiTile
          icon={CheckCircle2} tone="sage"
          label="Already Reviewed" value={int(summary?.already_reviewed)}
          sub="Status ≠ New" testId="kpi-already-reviewed"
          onClick={() => { setPresetFilter('reviewed'); setTab('tracker'); }}
        />
        <KpiTile
          icon={Clock} tone="sage"
          label="Scheduled" value={int(summary?.scheduled)}
          sub="Follow-up set" testId="kpi-scheduled"
          onClick={() => { setPresetFilter('scheduled'); setTab('tracker'); }}
        />
        <KpiTile
          icon={AlertTriangle} tone="red"
          label="Critically Aged" value={int(summary?.critically_aged)}
          sub="91+ days" testId="kpi-critical"
          onClick={() => { setPresetFilter('critical'); setTab('tracker'); }}
        />
        <KpiTile
          icon={Users} tone="lilac"
          label="Clients Affected" value={int(summary?.unique_clients)}
          sub="Distinct" testId="kpi-clients"
          onClick={() => { setPresetFilter('all'); setTab('tracker'); }}
        />
        <KpiTile
          icon={CheckCircle2} tone="sage"
          label="Appointments Reviewed" value={int(summary?.appts_reviewed)}
          sub="This snapshot" testId="kpi-reviewed"
          onClick={() => { setPresetFilter('all'); setTab('tracker'); }}
        />
      </div>

      {/* Payer-group breakdown + Top clients — the "who owes the most" view */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3" data-testid="up-breakdown-row">
        <div className="lg:col-span-3">
          <PayerGroupBars items={summary?.by_payer_group || []} />
        </div>
        <div className="lg:col-span-2">
          <TopClientsCard items={summary?.top_clients || []} />
        </div>
      </div>

      {/* Recovery Trend — running proof the effort is working */}
      <RecoveryTrendChart refreshKey={summary?.last_refresh?.finished_at || summary?.last_refresh?.at || ''} />

      {/* Missing-rate triage — one-click Fix per (payer, CPT) gap. */}
      <MissingRateQueue onFixed={() => { loadSummary(); }} />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b" style={{ borderColor: '#E8E5EC' }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 -mb-px border-b-2 font-semibold text-sm transition-colors ${
              tab === t.key
                ? 'border-[#685B72] text-[#2D2833]'
                : 'border-transparent text-[#685B72] hover:text-[#2D2833]'
            }`}
            data-testid={`up-tab-${t.key}`}
          >
            <t.icon size={14} className="inline mr-1.5" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'action'   && <ActionList config={config} dosFrom={dosFrom} dosTo={dosTo} payerGroupsFilter={payerGroupsFilter} minUnderpaid={minUnderpaid} onSaved={() => loadSummary()} />}
      {tab === 'tracker'  && <FullTracker config={config} dosFrom={dosFrom} dosTo={dosTo} payerGroupsFilter={payerGroupsFilter} onChangePayerGroups={setPayerGroupsFilter} minUnderpaid={minUnderpaid} preset={presetFilter} onClearPreset={() => setPresetFilter('all')} onSaved={() => loadSummary()} />}
      {tab === 'terminal' && <TerminalHistory onRestored={() => loadSummary()} />}
    </div>
  );
}

// ═══════════ Missing-rate exceptions triage ═══════════
// Each row = one (payer, CPT) gap. Clicking "Fix" resolves the payer to a
// Vault payer_id and opens a lightweight Add-Rate dialog pre-filled with
// the CPT + a sensible Effective From guess (earliest DOS affected). One
// save closes the whole group across every affected appt on the next
// refresh, because `run_refresh` re-evaluates detection from scratch.
function MissingRateQueue({ onFixed }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(null); // {raw_payer, code, earliest_dos}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/underpayment/exceptions', {
        params: { kind: 'missing_rate', grouped: true },
      });
      setItems(data.items || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || items.length === 0) return null;

  const totalAffected = items.reduce((s, r) => s + (r.affected_count || 0), 0);

  return (
    <Card className="p-4 border-[#E8E5EC]" data-testid="up-missing-rate-queue">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#F5EFE0] text-[#B08A3A] flex items-center justify-center">
          <AlertTriangle size={16} />
        </div>
        <div className="flex-1">
          <div className="font-serif text-lg text-[#2D2833]">Missing rate — needs a rate row</div>
          <div className="text-[11px] text-[#685B72]">
            {items.length} payer + CPT gap{items.length === 1 ? '' : 's'} · {totalAffected} appointment{totalAffected === 1 ? '' : 's'} waiting on a contract rate. Fix once, all clear next refresh.
          </div>
        </div>
      </div>
      <div className="overflow-auto max-h-[280px]">
        <table className="min-w-full text-sm" data-testid="up-missing-rate-table">
          <thead className="sticky top-0 bg-white text-left text-[10px] uppercase tracking-widest text-slate-500 border-b">
            <tr>
              <th className="py-2 pr-3">Payer (raw)</th>
              <th className="py-2 pr-3">CPT</th>
              <th className="py-2 pr-3 text-right">Appts</th>
              <th className="py-2 pr-3">Earliest DOS</th>
              <th className="py-2 pr-3">Latest DOS</th>
              <th className="py-2 pr-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(x => (
              <tr key={`${x.raw_payer}|${x.code}`}
                  className="border-b hover:bg-[#F1EDF5]/40"
                  data-testid={`up-missing-rate-row-${x.raw_payer}-${x.code}`.replace(/\s+/g,'-')}>
                <td className="py-2 pr-3 font-medium text-[#2D2833]">{x.raw_payer || 'Unknown'}</td>
                <td className="py-2 pr-3 font-mono text-xs">{x.code}</td>
                <td className="py-2 pr-3 text-right tabular-nums font-semibold text-[#8A3A3A]">{int(x.affected_count)}</td>
                <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{fmtDate(x.earliest_dos)}</td>
                <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{fmtDate(x.latest_dos)}</td>
                <td className="py-2 pr-3 text-right">
                  <Button
                    size="sm"
                    onClick={() => setFixing(x)}
                    className="h-7 bg-[#685B72] hover:bg-[#4A3B5F] text-white text-[11px] font-semibold"
                    data-testid={`up-missing-rate-fix-${x.raw_payer}-${x.code}`.replace(/\s+/g,'-')}
                  >
                    Fix rate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {fixing && (
        <QuickAddRateDialog
          exception={fixing}
          onClose={() => setFixing(null)}
          onSaved={() => {
            setFixing(null);
            load();
            onFixed && onFixed();
            toast.success('Rate saved — will clear from queue on next refresh');
          }}
        />
      )}
    </Card>
  );
}

// One-click "Fix rate" — resolves raw payer → Vault payer_id, then opens
// the Add-Rate form pre-filled. If no matching Vault payer, prompts the
// admin to first create one over in the Vault (deep link).
function QuickAddRateDialog({ exception, onClose, onSaved }) {
  const [resolving, setResolving] = useState(true);
  const [match, setMatch] = useState(null);
  const [alternates, setAlternates] = useState([]);
  const [confidence, setConfidence] = useState('none');
  const [payerId, setPayerId] = useState('');
  const [rate, setRate] = useState('');
  const [clientCharge, setClientCharge] = useState('');
  const [effFrom, setEffFrom] = useState(exception.earliest_dos ? exception.earliest_dos.slice(0,10) : '');
  const [notes, setNotes] = useState(`Auto-suggested from missing_rate exception (${exception.affected_count} appts affected)`);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/vault/payers/resolve', { params: { q: exception.raw_payer } });
        if (!alive) return;
        setMatch(data.match);
        setAlternates(data.alternates || []);
        setConfidence(data.confidence || 'none');
        if (data.match) setPayerId(data.match.payer_id);
      } catch { /* silent */ }
      finally { if (alive) setResolving(false); }
    })();
    return () => { alive = false; };
  }, [exception.raw_payer]);

  const save = async () => {
    if (!payerId) { toast.error('Pick a payer first'); return; }
    if (!rate)    { toast.error('Rate is required'); return; }
    if (!effFrom) { toast.error('Effective From is required'); return; }
    setSaving(true);
    try {
      await api.post('/vault/contract-rates', {
        payer_id: payerId,
        cpt_code: exception.code,
        contract_rate: rate,
        client_charge_full_ded: clientCharge || null,
        effective_date: effFrom,
        notes,
      });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const openVault = () => {
    // Same-tab navigation into Vault with the raw payer name pre-loaded
    // into a query param. PayerDirectory reads `?new=<raw>` on mount and
    // auto-opens the Add Payer dialog with the name filled in, so the
    // admin can save in one step and come right back to fix the rate.
    onClose();
    navigate(`/vault?tab=payers&new=${encodeURIComponent(exception.raw_payer || '')}`);
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="up-quick-add-rate-dialog">
        <DialogHeader>
          <DialogTitle>
            Fix rate · <span className="font-mono">{exception.code}</span>
          </DialogTitle>
          <DialogDescription>
            {exception.affected_count} appointment{exception.affected_count === 1 ? '' : 's'} for <b>{exception.raw_payer}</b> are waiting on a contract rate covering {fmtDate(exception.earliest_dos)} onward.
          </DialogDescription>
        </DialogHeader>

        {resolving ? (
          <div className="text-sm text-slate-500 py-4 text-center">Resolving payer…</div>
        ) : !match && alternates.length === 0 ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-[#F5EFE0] bg-[#F5EFE0]/40 p-3 text-sm text-[#2D2833]">
              No Vault payer matches "<b>{exception.raw_payer}</b>". Create the payer first, then come back here to add the rate.
            </div>
            <Button
              onClick={openVault}
              className="w-full bg-[#685B72] hover:bg-[#4A3B5F] text-white"
              data-testid="up-quick-add-rate-open-vault"
            >
              Open Vault → Create payer
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Payer</label>
              <select
                value={payerId} onChange={e => setPayerId(e.target.value)}
                className="h-9 w-full rounded-md border border-[#E8E5EC] px-2 bg-white text-sm"
                data-testid="up-quick-add-rate-payer"
              >
                {match && (
                  <option value={match.payer_id}>
                    {match.name}{confidence === 'exact' ? '  · exact match' : '  · best guess'}
                  </option>
                )}
                {alternates.map(a => (
                  <option key={a.payer_id} value={a.payer_id}>{a.name}</option>
                ))}
              </select>
              {confidence !== 'exact' && (
                <div className="text-[10px] text-[#685B72]">
                  Raw name "<i>{exception.raw_payer}</i>" doesn't exactly match a Vault payer — please confirm the pick above.
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Rate ($)</label>
                <Input
                  type="number" step="0.01" value={rate}
                  onChange={e => setRate(e.target.value)}
                  placeholder="e.g. 95.00"
                  data-testid="up-quick-add-rate-amount"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Client charge (full ded)</label>
                <Input
                  type="number" step="0.01" value={clientCharge}
                  onChange={e => setClientCharge(e.target.value)}
                  placeholder="optional"
                  data-testid="up-quick-add-rate-client"
                />
              </div>
              <div className="col-span-2 space-y-1">
                <label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Effective From</label>
                <Input
                  type="date" value={effFrom}
                  onChange={e => setEffFrom(e.target.value)}
                  data-testid="up-quick-add-rate-effective"
                />
                <div className="text-[10px] text-[#685B72]">
                  Pre-filled from the earliest affected DOS ({fmtDate(exception.earliest_dos)}). Any existing "current" rate for this payer + CPT will auto-close on the day before.
                </div>
              </div>
              <div className="col-span-2 space-y-1">
                <label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Notes</label>
                <Input
                  value={notes} onChange={e => setNotes(e.target.value)}
                  data-testid="up-quick-add-rate-notes"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="up-quick-add-rate-cancel">Cancel</Button>
          {match && (
            <Button
              onClick={save} disabled={saving || resolving}
              className="bg-[#685B72] hover:bg-[#4A3B5F] text-white"
              data-testid="up-quick-add-rate-save"
            >
              {saving ? 'Saving…' : 'Save rate'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KpiTile({ icon: Icon, label, value, sub, tone, testId, onClick }) {
  const toneMap = {
    red:   { bg: 'bg-[#F5EAEA]', text: 'text-[#8A3A3A]' },
    amber: { bg: 'bg-[#F5EFE0]', text: 'text-[#B08A3A]' },
    sage:  { bg: 'bg-[#E4EEE9]', text: 'text-[#3B6F5D]' },
    lilac: { bg: 'bg-[#EFE9F5]', text: 'text-[#685B72]' },
  }[tone] || { bg: 'bg-slate-100', text: 'text-slate-600' };
  return (
    <Card
      className={`p-4 border-[#E8E5EC] ${onClick ? 'cursor-pointer hover:border-[#685B72] hover:shadow-md transition' : ''}`}
      data-testid={testId}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className={`w-8 h-8 rounded-lg ${toneMap.bg} ${toneMap.text} flex items-center justify-center`}>
        <Icon size={16} />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold mt-2">{label}</div>
      <div className="font-serif text-2xl text-[#2D2833] mt-0.5">{value}</div>
      <div className="text-[11px] text-[#685B72] mt-0.5">{sub}</div>
    </Card>
  );
}

// ═══════════ Date-of-Service window ═══════════
// Narrows Summary Bar, Today's Action List, and Full Tracker to a chosen
// DOS window. Empty = "no bound" so a partial window (e.g. from-only)
// works. Presets cover the everyday desk views; the two native date
// inputs stay in ISO so the browser's picker works but the surrounding
// UI still speaks MM-DD-YYYY via a helper label.
function DosRangeBar({ dosFrom, dosTo, onChange }) {
  const [from, setFrom] = useState(dosFrom || '');
  const [to,   setTo]   = useState(dosTo   || '');

  // Keep local input state synced when a preset writes new values in.
  useEffect(() => { setFrom(dosFrom || ''); }, [dosFrom]);
  useEffect(() => { setTo(dosTo   || ''); }, [dosTo]);

  const apply = () => onChange(from, to);
  const clear = () => { setFrom(''); setTo(''); onChange('', ''); };

  const preset = (a, b) => { setFrom(a); setTo(b); onChange(a, b); };
  const today = new Date();
  const toIso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const addDays = (n) => { const d = new Date(today); d.setDate(d.getDate() + n); return d; };
  const startOfMonth = (d) => { const x = new Date(d); x.setDate(1); return x; };
  const startOfYear  = (d) => new Date(d.getFullYear(), 0, 1);

  const active = Boolean(dosFrom || dosTo);
  const rangeLabel = active
    ? `${fmtDate(dosFrom) || '—'}  →  ${fmtDate(dosTo) || '—'}`
    : 'All dates';

  return (
    <Card className="p-3 border-[#E8E5EC]" data-testid="up-dos-range">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">
          Date of service
        </div>
        <div
          className={`text-[11px] px-2 py-0.5 rounded-full ${
            active ? 'bg-[#EFE9F5] text-[#4A3B5F] font-semibold' : 'bg-slate-100 text-slate-500'
          }`}
          data-testid="up-dos-range-label"
        >
          {rangeLabel}
        </div>
        <div className="flex-1" />
        <label className="text-[11px] text-[#685B72]">From</label>
        <Input
          type="date"
          value={from} onChange={e => setFrom(e.target.value)}
          className="h-8 w-[145px] text-sm"
          data-testid="up-dos-from"
        />
        <label className="text-[11px] text-[#685B72]">To</label>
        <Input
          type="date"
          value={to} onChange={e => setTo(e.target.value)}
          className="h-8 w-[145px] text-sm"
          data-testid="up-dos-to"
        />
        <Button
          onClick={apply}
          className="h-8 bg-[#685B72] hover:bg-[#4A3B5F] text-white text-[12px] font-semibold"
          data-testid="up-dos-apply"
        >
          Apply
        </Button>
        <Button
          onClick={clear}
          variant="outline" className="h-8 text-[12px]"
          data-testid="up-dos-clear"
        >
          Clear
        </Button>
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        <PresetChip label="Last 30d"   onClick={() => preset(toIso(addDays(-30)), toIso(today))} testId="up-dos-preset-30" />
        <PresetChip label="Last 90d"   onClick={() => preset(toIso(addDays(-90)), toIso(today))} testId="up-dos-preset-90" />
        <PresetChip label="This month" onClick={() => preset(toIso(startOfMonth(today)), toIso(today))} testId="up-dos-preset-month" />
        <PresetChip label="YTD"        onClick={() => preset(toIso(startOfYear(today)), toIso(today))} testId="up-dos-preset-ytd" />
        <PresetChip label="Aged 91+d"  onClick={() => preset('', toIso(addDays(-91)))} testId="up-dos-preset-aged" />
      </div>
    </Card>
  );
}

// ═══════════ Focused Queue Bar (payer + min $ filters) ═══════════
// Cascades to summary / action list / tracker so counts, totals, and
// charts always reflect the active work-queue slice. Payer chips come
// from the summary payload's `by_payer_group` buckets (Cigna, BCBS,
// UHC, All Other) — the exact same taxonomy every downstream chart
// uses, so admin never sees a payer here that doesn't have a matching
// tile above.
function FocusedQueueBar({ payerGroups, onChangePayerGroups, minUnderpaid, onChangeMinUnderpaid, summaryPayerBuckets }) {
  const [localMin, setLocalMin] = useState(minUnderpaid || '');
  useEffect(() => { setLocalMin(minUnderpaid || ''); }, [minUnderpaid]);

  const buckets = ['Cigna', 'BCBS', 'UHC', 'All Other'];
  // Enrich each bucket with the current $ + count so admin can eyeball
  // "is it worth filtering to this one" before clicking.
  const bucketMeta = useMemo(() => {
    const m = {};
    for (const b of summaryPayerBuckets || []) m[b.group] = b;
    return m;
  }, [summaryPayerBuckets]);

  const togglePayer = (g) => {
    if (payerGroups.includes(g)) {
      onChangePayerGroups(payerGroups.filter(x => x !== g));
    } else {
      onChangePayerGroups([...payerGroups, g]);
    }
  };
  const clearAll = () => { onChangePayerGroups([]); onChangeMinUnderpaid(''); setLocalMin(''); };
  const applyMin = () => onChangeMinUnderpaid(localMin);

  const anyActive = payerGroups.length > 0 || (minUnderpaid && parseFloat(minUnderpaid) > 0);
  return (
    <Card className="p-3 border-[#E8E5EC]" data-testid="up-focus-bar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">
          Focus queue
        </div>
        <div
          className={`text-[11px] px-2 py-0.5 rounded-full ${
            anyActive ? 'bg-[#EFE9F5] text-[#4A3B5F] font-semibold' : 'bg-slate-100 text-slate-500'
          }`}
          data-testid="up-focus-label"
        >
          {anyActive
            ? `${payerGroups.length ? payerGroups.join(' + ') : 'All payers'}${minUnderpaid && parseFloat(minUnderpaid) > 0 ? ` · ≥ ${money(parseFloat(minUnderpaid))}` : ''}`
            : 'No filters — showing everything'}
        </div>
        <div className="flex-1" />
        <label className="text-[11px] text-[#685B72]">Min underpaid $</label>
        <Input
          type="number" min="0" step="1"
          value={localMin} onChange={e => setLocalMin(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') applyMin(); }}
          placeholder="e.g. 25"
          className="h-8 w-[110px] text-sm"
          data-testid="up-min-underpaid"
        />
        <Button onClick={applyMin} className="h-8 bg-[#685B72] hover:bg-[#4A3B5F] text-white text-[12px] font-semibold"
                data-testid="up-min-underpaid-apply">Apply</Button>
        <Button onClick={clearAll} variant="outline" className="h-8 text-[12px]" data-testid="up-focus-clear">Clear all</Button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="up-focus-payer-chips">
        {buckets.map(b => {
          const selected = payerGroups.includes(b);
          const meta = bucketMeta[b] || { underpaid: 0, count: 0 };
          return (
            <button
              key={b}
              onClick={() => togglePayer(b)}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                selected
                  ? 'bg-[#685B72] text-white border-[#685B72]'
                  : 'bg-white text-[#4A3B5F] border-[#E8E5EC] hover:bg-[#F6F2FA]'
              }`}
              data-testid={`up-focus-chip-${b.replace(/\s+/g, '-')}`}
            >
              {b}
              <span className={`ml-1.5 text-[10px] ${selected ? 'text-white/80' : 'text-[#685B72]'}`}>
                {meta.count} · {money(meta.underpaid)}
              </span>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function PresetChip({ label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="text-[11px] px-2.5 py-1 rounded-full bg-slate-50 hover:bg-[#EFE9F5] hover:text-[#4A3B5F] text-slate-600 border border-slate-200 transition-colors"
      data-testid={testId}
    >
      {label}
    </button>
  );
}

// ═══════════ Payer-group breakdown ═══════════
// Bar-style visual cards, one per bucket. Urgency-tuned: the bar width and
// tint deepen with $ so the biggest liability jumps at admin the moment
// they open the module. Brand palette:
//   plum      #685B72  (largest)
//   claret    #8A3A3A  (2nd)
//   ochre     #B08A3A  (3rd)
//   sage      #3B6F5D  (smallest)
const PAYER_TONE_BY_RANK = [
  { bg: '#4A3B5F', text: '#FFFFFF', accent: '#EFE9F5' }, // 1st - plum, boldest
  { bg: '#8A3A3A', text: '#FFFFFF', accent: '#F5EAEA' }, // 2nd - claret
  { bg: '#B08A3A', text: '#FFFFFF', accent: '#F5EFE0' }, // 3rd - ochre
  { bg: '#3B6F5D', text: '#FFFFFF', accent: '#E4EEE9' }, // 4th - sage
];
function PayerGroupBars({ items }) {
  // Sort by $ desc so the top ranked gets the boldest treatment. Skip the
  // sort side-effect on the caller by cloning.
  const ranked = [...(items || [])].sort((a, b) => (b.underpaid || 0) - (a.underpaid || 0));
  const max = Math.max(1, ...(ranked.map(x => x.underpaid || 0)));
  return (
    <Card className="p-4 border-[#E8E5EC] h-full" data-testid="up-payer-breakdown">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#EFE9F5] text-[#685B72] flex items-center justify-center">
          <DollarSign size={16} />
        </div>
        <div>
          <div className="font-serif text-lg text-[#2D2833]">Outstanding by payer</div>
          <div className="text-[11px] text-[#685B72]">Bigger bar = bigger liability. Go where the money is.</div>
        </div>
      </div>
      <div className="space-y-2">
        {ranked.map((row, i) => {
          const tone = PAYER_TONE_BY_RANK[i] || PAYER_TONE_BY_RANK[3];
          const pct = Math.max(4, Math.round(((row.underpaid || 0) / max) * 100));
          return (
            <div key={row.group} data-testid={`up-payer-row-${row.group.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="flex items-baseline justify-between text-xs mb-1">
                <div className="font-semibold text-[#2D2833]">{row.group}</div>
                <div className="tabular-nums text-[#685B72]">
                  {row.count} appt{row.count === 1 ? '' : 's'} · <span className="font-semibold text-[#2D2833]">{money(row.underpaid)}</span>
                </div>
              </div>
              <div className="h-6 rounded-md relative overflow-hidden" style={{ backgroundColor: tone.accent }}>
                <div
                  className="absolute inset-y-0 left-0 rounded-md transition-all duration-500"
                  style={{ width: `${pct}%`, backgroundColor: tone.bg }}
                />
              </div>
            </div>
          );
        })}
        {ranked.length === 0 && (
          <div className="text-sm text-slate-500 py-4 text-center">No open items yet — nothing to chase.</div>
        )}
      </div>
    </Card>
  );
}

// ═══════════ Top-10 clients ═══════════
// Ranked list — bigger $ get bolder rank badge + deeper red so admin can
// zero in on the biggest single recovery opportunities first.
function TopClientsCard({ items }) {
  const max = Math.max(1, ...(items || []).map(x => x.underpaid || 0));
  return (
    <Card className="p-4 border-[#E8E5EC] h-full" data-testid="up-top-clients">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#F5EAEA] text-[#8A3A3A] flex items-center justify-center">
          <Crown size={16} />
        </div>
        <div>
          <div className="font-serif text-lg text-[#2D2833]">Top 10 clients</div>
          <div className="text-[11px] text-[#685B72]">Ranked by outstanding $. Chase these first.</div>
        </div>
      </div>
      <div className="space-y-1.5">
        {(items || []).map((row, i) => {
          const share = (row.underpaid || 0) / max;
          const isTopThree = i < 3;
          return (
            <div
              key={row.client + i}
              className="flex items-center gap-2 py-1 px-2 rounded-md hover:bg-[#F1EDF5]/50"
              data-testid={`up-top-client-row-${i}`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${
                isTopThree ? 'bg-[#8A3A3A] text-white' : 'bg-[#EFE9F5] text-[#685B72]'
              }`}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#2D2833] truncate">{row.client}</div>
                <div className="h-1 bg-[#F1EDF5] rounded-full mt-1 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(4, Math.round(share * 100))}%`,
                      backgroundColor: isTopThree ? '#8A3A3A' : '#685B72',
                    }}
                  />
                </div>
              </div>
              <div className="text-right whitespace-nowrap">
                <div className="text-sm font-semibold tabular-nums text-[#8A3A3A]">{money(row.underpaid)}</div>
                <div className="text-[10px] text-[#685B72]">{row.appts} appt{row.appts === 1 ? '' : 's'}</div>
              </div>
            </div>
          );
        })}
        {(items || []).length === 0 && (
          <div className="text-sm text-slate-500 py-4 text-center">No clients to rank yet.</div>
        )}
      </div>
    </Card>
  );
}

// ═══════════ Recovery Trend Chart ═══════════
// One point per refresh snapshot. If the line is falling, admin can prove
// the recovery effort works. Flat/rising = time to escalate.
function RecoveryTrendChart({ refreshKey }) {
  const [snaps, setSnaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoomDate, setZoomDate] = useState(null); // 'YYYY-MM-DD' clicked
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/underpayment/trend');
        if (alive) setSnaps(data.snapshots || []);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [refreshKey]);

  // Iter 232 (user directive 2026-02-04) — display-filter the trend
  // to at most two dots per ISO week (Monday + Friday) covering the
  // last 2 months. If the exact Monday or Friday snapshot is missing
  // for that week, fall back to the closest available snapshot from
  // that week (nearest same-week day-of-week). The full raw snapshot
  // history is still stored + fetched — this only affects what's drawn.
    // Iter 255 (timezone audit): bucket every snapshot by its Central
    // calendar day, not the UTC-day prefix of the ISO string. A refresh
    // that ran at 23:15 CT posts as `+05:15 next-day` UTC; the old
    // `slice(0, 10)` grouped it under the wrong day and shifted the
    // whole trend axis by 24h.
    const chartData = useMemo(() => {
    // 1. Collapse same-day duplicates (multiple refreshes on one date
    //    keep only the latest, matching the pre-iter232 behaviour).
    const byDay = new Map();
    for (const s of snaps) {
      const day = ctDateKey(s.at);
      if (!day) continue;
      byDay.set(day, {
        at: s.at,
        day,
        label: fmtDate(s.at),
        open: Number(s.open_dollars) || 0,
        count: Number(s.open_count) || 0,
      });
    }
    if (byDay.size === 0) return [];
    const sorted = Array.from(byDay.values())
      .sort((a, b) => a.day.localeCompare(b.day));
    // 2. Trim to the last 2 months. Anchor to the newest snapshot in
    //    the data (not today's clock) so a paused pipeline doesn't
    //    black out the whole chart.
    const anchor = new Date(sorted[sorted.length - 1].day + 'T00:00:00Z');
    const cutoff = new Date(anchor); cutoff.setUTCMonth(cutoff.getUTCMonth() - 2);
    const inWindow = sorted.filter(p =>
      new Date(p.day + 'T00:00:00Z') >= cutoff);
    if (inWindow.length === 0) return [];
    // 3. Bucket by ISO week (Mon-anchored) in Central time. For each
    //    bucket, pick the Monday snapshot (day-of-week=1) and the
    //    Friday snapshot (day-of-week=5). If either is missing,
    //    substitute the snapshot from that week whose weekday is
    //    CLOSEST to the target (|dow - target|; ties break earliest).
    const dowOf = (day) => ctWeekdayIso(day + 'T12:00:00Z');
    const weekKey = (day) => {
      // Monday of the ISO week the day belongs to.
      const [y, m, dd] = day.split('-').map(Number);
      const d = new Date(Date.UTC(y, m - 1, dd, 12));
      const dow = dowOf(day);
      d.setUTCDate(d.getUTCDate() - (dow - 1));
      return d.toISOString().slice(0, 10);
    };
    const buckets = new Map();
    for (const p of inWindow) {
      const wk = weekKey(p.day);
      if (!buckets.has(wk)) buckets.set(wk, []);
      buckets.get(wk).push({ ...p, dow: dowOf(p.day) });
    }
    const pickClosest = (points, targetDow) => {
      // Deterministic: minimum |dow - target|; earliest day breaks ties.
      let best = null; let bestScore = Infinity;
      for (const p of points) {
        const score = Math.abs(p.dow - targetDow);
        if (score < bestScore || (score === bestScore && p.day < best.day)) {
          best = p; bestScore = score;
        }
      }
      return best;
    };
    const out = [];
    const seen = new Set();
    // Iterate buckets in chronological order so the line renders L→R.
    for (const [, points] of Array.from(buckets.entries()).sort(
      (a, b) => a[0].localeCompare(b[0]))) {
      for (const targetDow of [1, 5]) {  // Monday first, Friday second
        const exact = points.find(p => p.dow === targetDow);
        const chosen = exact || pickClosest(points, targetDow);
        if (chosen && !seen.has(chosen.day)) {
          seen.add(chosen.day);
          out.push(chosen);
        }
      }
    }
    return out;
  }, [snaps]);

  const delta = useMemo(() => {
    if (chartData.length < 2) return null;
    const first = chartData[0].open;
    const last = chartData[chartData.length - 1].open;
    return { first, last, diff: last - first, pct: first === 0 ? 0 : ((last - first) / first) * 100 };
  }, [chartData]);

  const trendDown = delta && delta.diff < 0;
  const trendUp = delta && delta.diff > 0;

  return (
    <Card className="p-4 border-[#E8E5EC]" data-testid="up-trend-chart">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          trendDown ? 'bg-[#E4EEE9] text-[#3B6F5D]' :
          trendUp   ? 'bg-[#F5EAEA] text-[#8A3A3A]' :
                      'bg-[#EFE9F5] text-[#685B72]'
        }`}>
          <TrendingDown size={16} className={trendDown ? '' : 'rotate-180'} />
        </div>
        <div className="flex-1">
          <div className="font-serif text-lg text-[#2D2833]">Recovery trend</div>
          <div className="text-[11px] text-[#685B72]">
            Outstanding $ after each refresh. Falling = recovery working. Flat / climbing = push harder.
          </div>
        </div>
        {delta && (
          <div className="text-right" data-testid="up-trend-delta">
            <div className={`text-sm font-semibold tabular-nums ${
              trendDown ? 'text-[#3B6F5D]' : trendUp ? 'text-[#8A3A3A]' : 'text-[#685B72]'
            }`}>
              {trendDown ? '↓' : trendUp ? '↑' : '→'} {money(Math.abs(delta.diff))}
            </div>
            <div className="text-[10px] text-[#685B72]">
              vs first snapshot {delta.pct !== 0 ? `(${delta.pct > 0 ? '+' : ''}${delta.pct.toFixed(1)}%)` : ''}
            </div>
          </div>
        )}
      </div>
      {loading ? (
        <div className="h-40 flex items-center justify-center text-sm text-slate-400">Loading trend…</div>
      ) : chartData.length === 0 ? (
        <div className="h-40 flex items-center justify-center text-sm text-slate-400" data-testid="up-trend-empty">
          No snapshots yet. Run a refresh to lay down the first data point — the timeline builds from there.
        </div>
      ) : chartData.length === 1 ? (
        <div className="h-40 flex flex-col items-center justify-center text-sm text-slate-500">
          <div className="text-2xl font-serif text-[#2D2833]">{money(chartData[0].open)}</div>
          <div className="text-[11px] mt-1">First snapshot captured {chartData[0].label}. Second refresh will start the trend line.</div>
        </div>
      ) : (
        <div style={{ width: '100%', height: 220 }} data-testid="up-trend-svg">
          <ResponsiveContainer>
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
              style={{ cursor: 'pointer' }}
            >
              <defs>
                <linearGradient id="upTrendFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#685B72" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#685B72" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E5EC" vertical={false} />
              <XAxis
                dataKey="label" tick={{ fontSize: 11, fill: '#685B72' }}
                axisLine={{ stroke: '#E8E5EC' }} tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#685B72' }}
                axisLine={{ stroke: '#E8E5EC' }} tickLine={false}
                tickFormatter={(v) => `$${Math.round(v/1000)}k`}
                width={50}
              />
              <Tooltip
                formatter={(v, n) => n === 'open' ? [money(v), 'Outstanding'] : [v, n]}
                labelStyle={{ color: '#2D2833', fontWeight: 600, fontSize: 12 }}
                contentStyle={{ borderRadius: 8, border: '1px solid #E8E5EC', fontSize: 12 }}
              />
              <Area
                type="monotone" dataKey="open"
                stroke="#4A3B5F" strokeWidth={2}
                fill="url(#upTrendFill)"
                // Recharts 3.x: onClick on AreaChart no longer passes
                // activePayload — wire the handler at the dot level where
                // the datum is delivered directly. `activeDot.onClick` fires
                // on the hovered/tapped point; `dot.onClick` covers cold
                // clicks on any raw dot.
                dot={(props) => {
                  const { cx, cy, payload, index } = props;
                  return (
                    <circle
                      key={index}
                      cx={cx} cy={cy} r={4}
                      fill="#4A3B5F"
                      style={{ cursor: 'pointer' }}
                      data-testid={`up-trend-dot-${payload?.day || index}`}
                      onClick={() => payload?.day && setZoomDate(payload.day)}
                    />
                  );
                }}
                activeDot={(props) => {
                  const { cx, cy, payload, index } = props;
                  return (
                    <circle
                      key={`active-${index}`}
                      cx={cx} cy={cy} r={6}
                      fill="#4A3B5F"
                      style={{ cursor: 'pointer' }}
                      onClick={() => payload?.day && setZoomDate(payload.day)}
                    />
                  );
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      {chartData.length > 0 && (
        <div className="text-[10px] text-[#685B72] mt-2 text-right">
          Tip · click any dot to see every refresh captured that day.
        </div>
      )}
      {zoomDate && (
        <TrendZoomDialog date={zoomDate} onClose={() => setZoomDate(null)} />
      )}
    </Card>
  );
}

// ═══════════ Trend Zoom Dialog ═══════════
// Drilldown: every raw underpayment_snapshots row for a single calendar date.
// Answers "we refreshed 6 times today — what changed between them?"
function TrendZoomDialog({ date, onClose }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get('/underpayment/trend/day', { params: { date } });
        if (alive) setRows(data.snapshots || []);
      } catch {
        if (alive) setRows([]);
        toast.error('Could not load same-day refresh detail');
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [date]);

  const timeOnly = (iso) => {
    const d = new Date(iso);
    if (isNaN(d)) return String(iso || '').slice(11, 16);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  const buckets = ['Cigna', 'BCBS', 'UHC', 'All Other'];

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="up-trend-zoom-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-[#2D2833]">
            Refreshes on {fmtDate(date)}
          </DialogTitle>
          <DialogDescription>
            Every snapshot captured that day, in order. Watch outstanding $ move
            between refreshes to see what each pass recovered.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-slate-500 p-4">Loading…</div>
        ) : (rows || []).length === 0 ? (
          <div className="text-sm text-slate-500 p-4" data-testid="up-trend-zoom-empty">
            No snapshots recorded for {fmtDate(date)}.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[60vh]" data-testid="up-trend-zoom-table">
            <table className="w-full text-xs">
              <thead className="text-[#685B72] border-b border-[#E8E5EC]">
                <tr>
                  <th className="text-left py-2 pr-3">Time</th>
                  <th className="text-right py-2 pr-3">Open $</th>
                  <th className="text-right py-2 pr-3">Open #</th>
                  <th className="text-right py-2 pr-3">New</th>
                  <th className="text-right py-2 pr-3">Auto-resolved</th>
                  <th className="text-right py-2 pr-3">Archived</th>
                  <th className="text-left py-2 pr-3">Actor</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s, i) => (
                  <tr key={i} className="border-b border-[#F1EEF3] hover:bg-[#FAF8FC]">
                    <td className="py-2 pr-3 tabular-nums">{timeOnly(s.at)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums font-semibold text-[#2D2833]">{money(s.open_dollars)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{int(s.open_count)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{int(s.new_rows)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-[#3B6F5D]">{int(s.auto_resolved)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-[#8A3A3A]">{int(s.archived)}</td>
                    <td className="py-2 pr-3 text-[#685B72] truncate max-w-[180px]" title={s.actor || ''}>{s.actor || 'system'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 0 && rows[0].by_payer_group && (
              <div className="mt-4 pt-3 border-t border-[#E8E5EC]">
                <div className="text-[11px] text-[#685B72] mb-2">Latest per-payer split (from the last refresh that day)</div>
                <div className="grid grid-cols-4 gap-2">
                  {buckets.map(b => {
                    const g = (rows[rows.length - 1].by_payer_group || {})[b] || {};
                    return (
                      <div key={b} className="border border-[#E8E5EC] rounded-lg p-2">
                        <div className="text-[10px] text-[#685B72]">{b}</div>
                        <div className="text-sm font-semibold tabular-nums text-[#2D2833]">{money(g.underpaid)}</div>
                        <div className="text-[10px] text-[#685B72]">{int(g.count)} rows</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="up-trend-zoom-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════ Action List ═══════════
function ActionList({ config, dosFrom, dosTo, payerGroupsFilter, minUnderpaid, onSaved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dosFrom) params.dos_from = dosFrom;
      if (dosTo)   params.dos_to   = dosTo;
      if (payerGroupsFilter && payerGroupsFilter.length) params.payer_groups = payerGroupsFilter.join(',');
      if (minUnderpaid && parseFloat(minUnderpaid) > 0)   params.min_underpaid = parseFloat(minUnderpaid);
      const { data } = await api.get('/underpayment/action-list', { params });
      setRows(data.items || []);
    } catch { toast.error('Could not load action list'); }
    finally { setLoading(false); }
  }, [dosFrom, dosTo, payerGroupsFilter, minUnderpaid]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-4 border-[#E8E5EC]" data-testid="up-action-list">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-serif text-xl text-[#2D2833]">Today's action list</div>
          <div className="text-xs text-[#685B72] mt-0.5">{rows.length} item{rows.length === 1 ? '' : 's'} need a touch today. Sorted by highest dollar impact.</div>
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-slate-500 p-4">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-center gap-2" data-testid="up-action-empty">
          <CheckCircle2 size={16} /> All clear — no underpayments need action today.
        </div>
      ) : (
        <TrackerTable rows={rows} onOpen={setEditing} />
      )}
      {editing && (
        <RowEditor
          row={editing}
          config={config}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onSaved(); }}
        />
      )}
    </Card>
  );
}

// ═══════════ Full Tracker ═══════════

// Multi-select payer filter. Renders as a compact popover with a
// searchable checkbox list; every option shows the payer's outstanding
// $ + open count so admin can zero in on the biggest liability in one
// look. Selection is committed instantly to the shared payerGroupsFilter
// state so the summary bar, payer breakdown, top-clients card, and
// recovery-trend chart update in lockstep.
function PayerFilterDropdown({ options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = React.useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    const list = options || [];
    if (!q) return list;
    return list.filter(o => (o.payer_group || '').toLowerCase().includes(q));
  }, [options, search]);
  const toggle = (name) => {
    if (selected.includes(name)) onChange(selected.filter(x => x !== name));
    else onChange([...selected, name]);
  };
  const clearAll = () => onChange([]);
  const label = selected.length === 0
    ? 'All payers'
    : selected.length === 1
      ? selected[0]
      : `${selected.length} payers`;
  return (
    <div ref={containerRef} className="relative" data-testid="up-tracker-payer-filter">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="h-9 px-3 pr-8 rounded-md border text-sm bg-white text-left min-w-[160px] relative"
        style={{ borderColor: selected.length ? '#685B72' : '#E8E5EC', color: selected.length ? '#2D2833' : '#4A3B5F', fontWeight: selected.length ? 600 : 400 }}
        data-testid="up-tracker-payer-filter-btn"
      >
        {label}
        <span className="absolute right-2.5 top-2.5 text-slate-400 text-xs">▾</span>
      </button>
      {open && (
        <div
          className="absolute z-30 mt-1 w-[280px] bg-white rounded-lg shadow-lg border p-2"
          style={{ borderColor: '#E8E5EC' }}
          data-testid="up-tracker-payer-filter-panel"
        >
          <div className="flex items-center gap-2 mb-2">
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search payer…"
              className="h-8 text-sm"
              data-testid="up-tracker-payer-filter-search"
              autoFocus
            />
            <Button
              variant="outline"
              size="sm"
              onClick={clearAll}
              disabled={selected.length === 0}
              className="h-8 text-[12px]"
              data-testid="up-tracker-payer-filter-clear"
            >
              Clear
            </Button>
          </div>
          <div className="max-h-[280px] overflow-auto -mx-2">
            {filtered.length === 0 && (
              <div className="text-xs text-slate-500 py-4 text-center">No payers match “{search}”.</div>
            )}
            {filtered.map((p) => {
              const isSel = selected.includes(p.payer_group);
              return (
                <label
                  key={p.payer_group}
                  className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer text-sm hover:bg-[#F6F2FA] ${isSel ? 'bg-[#EFE9F5]' : ''}`}
                  data-testid={`up-tracker-payer-opt-${(p.payer_group || 'unknown').toString().toLowerCase().replace(/\s+/g,'-')}`}
                >
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggle(p.payer_group)}
                    className="h-3.5 w-3.5"
                  />
                  <span className="flex-1 truncate text-[#2D2833]">{p.payer_group}</span>
                  <span className="text-[11px] text-[#685B72] tabular-nums">
                    {p.count} · {money(p.underpaid)}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function FullTracker({ config, dosFrom, dosTo, payerGroupsFilter, onChangePayerGroups, minUnderpaid, preset, onClearPreset, onSaved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [action, setAction] = useState('');
  const [sort, setSort] = useState('underpaid_desc');
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  // Distinct payers with open items — powers the payer filter dropdown.
  // Refreshed alongside the DOS window so the option list mirrors what
  // the summary bar is counting.
  const [payerOptions, setPayerOptions] = useState([]);
  useEffect(() => {
    const params = {};
    if (dosFrom) params.dos_from = dosFrom;
    if (dosTo)   params.dos_to   = dosTo;
    api.get('/underpayment/payers', { params })
      .then(r => setPayerOptions(r.data?.payers || []))
      .catch(() => setPayerOptions([]));
  }, [dosFrom, dosTo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (q) params.q = q;
      if (payerGroupsFilter && payerGroupsFilter.length) params.payer_groups = payerGroupsFilter.join(',');
      if (minUnderpaid && parseFloat(minUnderpaid) > 0)   params.min_underpaid = parseFloat(minUnderpaid);
      if (status) params.status = status;
      if (action) params.action = action;
      // Iter 261 — top-tile preset cascades into the tracker list. When
      // the user clears the badge above the table, preset resets to
      // 'all' and the param is omitted, restoring the unfiltered view.
      if (preset && preset !== 'all') params.preset = preset;
      if (dosFrom) params.dos_from = dosFrom;
      if (dosTo)   params.dos_to   = dosTo;
      const { data } = await api.get('/underpayment/tracker', { params });
      setRows(data.items || []);
      // Prune stale selections that are no longer in the visible set.
      setSelected(prev => {
        const visible = new Set((data.items || []).map(r => r.appt_key));
        const next = new Set();
        prev.forEach(k => { if (visible.has(k)) next.add(k); });
        return next;
      });
    } catch { toast.error('Could not load tracker'); }
    finally { setLoading(false); }
  }, [q, status, action, sort, dosFrom, dosTo, payerGroupsFilter, minUnderpaid, preset]);
  useEffect(() => { load(); }, [load]);

  const toggleRow = useCallback((k) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }, []);
  const toggleAll = useCallback(() => {
    setSelected(prev => {
      if (prev.size === rows.length && rows.length > 0) return new Set();
      return new Set(rows.map(r => r.appt_key));
    });
  }, [rows]);
  const clearSelection = useCallback(() => setSelected(new Set()), []);

  return (
    <Card className="p-4 border-[#E8E5EC]" data-testid="up-full-tracker">
      {/* Iter 261 — active-preset badge above the Full Tracker.
          Shows exactly which top-summary tile the user clicked and
          gives them one obvious way to clear it. Only renders when
          `preset` is a non-default value. */}
      {preset && preset !== 'all' && (
        <div
          className="flex items-center justify-between gap-3 mb-3 rounded-md border border-[#B08A3A55] bg-[#FFF7E6] px-3 py-2"
          data-testid="up-active-preset-banner"
        >
          <div className="text-[12px] text-[#8A6A1F] flex items-center gap-2">
            <span className="uppercase tracking-widest font-semibold text-[10px]">Filter</span>
            <span data-testid="up-active-preset-label">
              {preset === 'reviewed'  && 'Already Reviewed (status ≠ New)'}
              {preset === 'scheduled' && 'Scheduled (follow-up date set)'}
              {preset === 'critical'  && 'Critically Aged (91+ days)'}
            </span>
          </div>
          <button
            onClick={onClearPreset}
            className="text-[11px] font-semibold text-[#8A6A1F] hover:text-[#2D2833] underline"
            data-testid="up-active-preset-clear"
          >
            Clear filter
          </button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search client, payer, code, ref/claim…"
            className="pl-8 h-9 text-sm"
            data-testid="up-search"
          />
        </div>
        <Select value={status} onChange={setStatus} options={['', ...(config?.statuses || [])]} placeholder="Any status" tid="up-filter-status" />
        <Select value={action} onChange={setAction} options={['', 'Action', 'Scheduled']} placeholder="Any action" tid="up-filter-action" />
        <PayerFilterDropdown
          options={payerOptions}
          selected={payerGroupsFilter || []}
          onChange={onChangePayerGroups}
        />
        <Select
          value={sort} onChange={setSort} tid="up-sort"
          options={[
            'underpaid_desc', 'underpaid_asc',
            'days_desc', 'days_asc',
            'client_asc', 'client_desc',
            'payer_asc', 'payer_desc',
            'dos_desc', 'dos_asc',
            'status_asc', 'follow_asc',
          ]}
          labels={{
            underpaid_desc: 'Sort: Underpaid $ ↓',
            underpaid_asc:  'Sort: Underpaid $ ↑',
            days_desc:      'Sort: Days aged ↓',
            days_asc:       'Sort: Days aged ↑',
            client_asc:     'Sort: Client A → Z',
            client_desc:    'Sort: Client Z → A',
            payer_asc:      'Sort: Payer A → Z',
            payer_desc:     'Sort: Payer Z → A',
            dos_desc:       'Sort: DOS newest',
            dos_asc:        'Sort: DOS oldest',
            status_asc:     'Sort: Status',
            follow_asc:     'Sort: Follow-up date',
          }}
        />
        <div className="text-xs text-slate-500 ml-auto whitespace-nowrap">{loading ? 'Loading…' : `${rows.length} open`}</div>
      </div>

      {/* Bulk-actions toolbar — only visible when 1+ rows are checked. */}
      {selected.size > 0 && (
        <div
          className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-[#F1EDF5] border border-[#E8E5EC]"
          data-testid="up-bulk-toolbar"
        >
          <span className="text-sm font-semibold text-[#2D2833]" data-testid="up-bulk-count">
            {selected.size} selected
          </span>
          <Button
            size="sm"
            onClick={() => setBulkOpen(true)}
            className="h-8 bg-[#685B72] hover:bg-[#4A3B5F] text-white text-[12px] font-semibold"
            data-testid="up-bulk-edit-btn"
          >
            Bulk edit {selected.size}
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={clearSelection}
            className="h-8 text-[12px]"
            data-testid="up-bulk-clear-btn"
          >
            Clear
          </Button>
          <div className="text-[11px] text-[#685B72] ml-auto">
            Apply one status / disposition / owner / follow-up to every selected appointment.
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 p-4">Loading…</div>
      ) : (
        <TrackerTable
          rows={rows}
          onOpen={setEditing}
          selectable
          selected={selected}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
        />
      )}
      {editing && (
        <RowEditor
          row={editing}
          config={config}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); onSaved(); }}
        />
      )}
      {bulkOpen && (
        <BulkEditor
          count={selected.size}
          apptKeys={Array.from(selected)}
          config={config}
          onClose={() => setBulkOpen(false)}
          onSaved={() => {
            setBulkOpen(false);
            clearSelection();
            load();
            onSaved();
          }}
        />
      )}
    </Card>
  );
}


// ─── Bulk Restore dialog (super_user only) ─────────────────────────
// Iter 245c UI — surfaces the /terminal/bulk-restore-auto-resolved
// endpoint the user has been triggering from DevTools. Type-to-confirm
// gate ("RESTORE") stops the double-click double-restore that got them
// into the loop last time.
function BulkRestoreDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [since, setSince] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setConfirmText(''); setRunning(false); setResult(null); setSince(''); };

  // Resolved ISO cutoff shown in the confirmation step. Empty when
  // no date is picked yet so the RESTORE button stays disabled and
  // the user sees an explicit prompt to choose.
  const resolvedCutoff = since ? `${since}T00:00:00` : '';

  const submit = async () => {
    if (confirmText !== 'RESTORE' || running || !since) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post(
        `/underpayment/terminal/bulk-restore-auto-resolved?since=${encodeURIComponent(resolvedCutoff)}`,
      );
      setResult(data);
      toast.success(`Restored ${data.restored} row${data.restored === 1 ? '' : 's'}.`);
      onDone && onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Bulk restore failed';
      setResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => { reset(); setOpen(true); }}
        className="h-9 text-[12px] text-[#4A3B5F] border-[#D4C9E1] hover:bg-[#EFEAF3]"
        data-testid="up-bulk-restore-open"
        title="Bulk restore every auto_resolved row since a date"
      >
        <Archive size={13} className="mr-1.5" /> Bulk Restore
      </Button>
      <Dialog open={open} onOpenChange={(o) => { if (!running) { setOpen(o); if (!o) reset(); } }}>
        <DialogContent className="max-w-md" data-testid="up-bulk-restore-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Restore Auto-Resolved Rows</DialogTitle>
            <DialogDescription>
              Restore every Terminal-History row that was <b>auto-archived</b> on or after this
              date back into the active tracker. Manually resolved rows (with notes/owner set)
              are never touched.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-[#685B72]">
                Restore rows archived since
              </label>
              <Input
                type="date"
                value={since}
                onChange={(e) => setSince(e.target.value)}
                disabled={running}
                className="mt-1 h-9"
                data-testid="up-bulk-restore-date"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                {since
                  ? `Interpreted as ${resolvedCutoff} UTC. Anything archived before this stays put.`
                  : 'Pick a date to enable the restore.'}
              </div>
            </div>

            <div
              className="rounded-md border border-[#D4C9E1] bg-[#F5F1F8] p-3 text-xs text-[#4A3B5F]"
              data-testid="up-bulk-restore-cutoff-summary"
            >
              <div className="font-semibold uppercase tracking-wide text-[10px] text-[#685B72]">
                Cutoff to be sent
              </div>
              <div
                className="font-mono mt-0.5 text-[13px]"
                data-testid="up-bulk-restore-cutoff-value"
              >
                {resolvedCutoff || '— pick a date above —'}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-[#685B72]">
                Type <span className="font-mono text-[#8A3A3A]">RESTORE</span> to enable the button
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                disabled={running}
                placeholder="RESTORE"
                className="mt-1 h-9 font-mono"
                data-testid="up-bulk-restore-confirm-text"
              />
            </div>

            {running && (
              <div
                className="flex items-center gap-2 text-sm text-[#4A3B5F] bg-[#EFEAF3] rounded-md p-3"
                data-testid="up-bulk-restore-progress"
              >
                <RefreshCw size={14} className="animate-spin" />
                Restoring… this can take a moment on large batches.
              </div>
            )}

            {result && (
              <div
                className={`text-sm rounded-md p-3 ${result.ok === false ? 'bg-[#F5EAEA] text-[#8A3A3A]' : 'bg-[#E4EEE9] text-[#3B6F5D]'}`}
                data-testid="up-bulk-restore-result"
              >
                {result.ok === false ? (
                  <>Error: <span className="font-mono">{result.error}</span></>
                ) : (
                  <>
                    <div className="font-semibold">
                      <CheckCircle2 size={14} className="inline mr-1" />
                      Restored <b data-testid="up-bulk-restore-restored-count">{result.restored}</b> row{result.restored === 1 ? '' : 's'}
                    </div>
                    <div className="text-xs mt-1">
                      Skipped: <b data-testid="up-bulk-restore-skipped-count">{result.skipped || 0}</b>
                      {' · '}
                      Errors: <b data-testid="up-bulk-restore-error-count">{(result.errors || []).length}</b>
                      {' · '}
                      Since: <span className="font-mono">{result.since}</span>
                    </div>
                    {(result.errors || []).length > 0 && (
                      <details className="mt-2 text-xs">
                        <summary className="cursor-pointer">Show {(result.errors || []).length} error detail(s)</summary>
                        <ul className="mt-1 ml-4 list-disc">
                          {(result.errors || []).slice(0, 20).map((e, i) => (
                            <li key={i} className="font-mono truncate">{e.appt_key}: {e.error}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { if (!running) { setOpen(false); reset(); } }}
              disabled={running}
              data-testid="up-bulk-restore-cancel"
            >
              {result ? 'Close' : 'Cancel'}
            </Button>
            <Button
              onClick={submit}
              disabled={confirmText !== 'RESTORE' || running || !!result || !since}
              className="bg-[#4A3B5F] hover:bg-[#3B2E4C] text-white"
              data-testid="up-bulk-restore-submit"
            >
              {running ? 'Restoring…' : 'Bulk Restore'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ═══════════ Terminal ═══════════
function TerminalHistory({ onRestored }) {
  const { user } = useAuth();
  const isSuperUser = user?.role === 'super_user';
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null); // appt_key currently restoring

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      const { data } = await api.get('/underpayment/terminal', { params });
      setRows(data.items || []);
    } catch { toast.error('Could not load terminal history'); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const restore = async (row) => {
    if (!window.confirm(
      `Restore this appointment back into the active tracker?\n\n` +
      `Client: ${row.client}\nDOS: ${fmtDate(row.dos)}\nWas marked: ${row.final_status || 'Resolved'}\n\n` +
      `The row will reappear on the active tracker with its original status ("${row.final_status || 'New'}") so you can rework it. ` +
      `Notes, disposition, and owner are preserved.`
    )) return;
    setRestoring(row.appt_key);
    try {
      await api.post(`/underpayment/terminal/${row.appt_key}/restore`);
      toast.success(`Restored — ${row.client} is back on the tracker.`);
      load();
      onRestored && onRestored();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    } finally { setRestoring(null); }
  };

  return (
    <Card className="p-4 border-[#E8E5EC]" data-testid="up-terminal">
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1">
          <div className="font-serif text-xl text-[#2D2833]">Terminal history</div>
          <div className="text-xs text-[#685B72] mt-0.5">Read-only archive. Every row here is suppressed on future refreshes — no re-flagging, ever.</div>
        </div>
        {isSuperUser && (
          <BulkRestoreDialog onDone={() => { load(); onRestored && onRestored(); }} />
        )}
        <div className="relative w-[280px]">
          <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search terminal…" className="pl-8 h-9 text-sm" data-testid="up-terminal-search" />
        </div>
        <div className="text-xs text-slate-500 whitespace-nowrap">{loading ? 'Loading…' : `${rows.length} archived`}</div>
      </div>
      {loading ? <div className="text-sm text-slate-500 p-4">Loading…</div> : (
        <div className="overflow-auto max-h-[65vh]">
          <table className="min-w-full text-sm" data-testid="up-terminal-table">
            <thead className="sticky top-0 bg-white text-left text-[10px] uppercase tracking-widest text-slate-500 border-b">
              <tr>
                <th className="py-2 pr-3">Client</th>
                <th className="py-2 pr-3">DOS</th>
                <th className="py-2 pr-3">Code</th>
                <th className="py-2 pr-3">Payer</th>
                <th className="py-2 pr-3">Final Status</th>
                <th className="py-2 pr-3">Resolved On</th>
                <th className="py-2 pr-3">Underpaid at Close</th>
                <th className="py-2 pr-3">Notes</th>
                <th className="py-2 pr-3 text-right">Undo</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.appt_key} className="border-b hover:bg-slate-50" data-testid={`up-terminal-row-${r.appt_key}`}>
                  <td className="py-2 pr-3 font-medium text-[#2D2833]">{r.client}</td>
                  <td className="py-2 pr-3 text-slate-600">{fmtDate(r.dos)}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.code}</td>
                  <td className="py-2 pr-3 text-slate-600">{r.raw_payer || r.payer_group}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${r.final_status === 'Written Off' ? 'bg-[#F5EAEA] text-[#8A3A3A]' : 'bg-[#E4EEE9] text-[#3B6F5D]'}`}>
                      {r.final_status || 'Resolved'}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-slate-600">{fmtDate(r.resolution_date)}</td>
                  <td className="py-2 pr-3 text-slate-600">{money(r.underpaid_at_close)}</td>
                  <td className="py-2 pr-3 text-slate-600 truncate max-w-[240px]" title={r.notes || ''}>{r.notes || '—'}</td>
                  <td className="py-2 pr-3 text-right">
                    <Button
                      size="sm" variant="outline"
                      disabled={restoring === r.appt_key}
                      onClick={() => restore(r)}
                      className="h-7 text-[11px] text-[#4A3B5F] hover:bg-[#EFEAF3]"
                      data-testid={`up-terminal-restore-${r.appt_key}`}
                      title="Restore to tracker — I marked this by mistake"
                    >
                      {restoring === r.appt_key ? 'Restoring…' : 'Restore'}
                    </Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-slate-400">Terminal history is empty.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ═══════════ Shared bits ═══════════
function TrackerTable({ rows, onOpen, selectable = false, selected, onToggleRow, onToggleAll }) {
  const allChecked = selectable && rows.length > 0 && selected && selected.size === rows.length;
  const someChecked = selectable && selected && selected.size > 0 && !allChecked;
  return (
    <div className="overflow-auto max-h-[65vh]">
      <table className="min-w-full text-sm" data-testid="up-tracker-table">
        <thead className="sticky top-0 bg-white text-left text-[10px] uppercase tracking-widest text-slate-500 border-b">
          <tr>
            {selectable && (
              <th className="py-2 pr-3 w-8">
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={!!allChecked}
                  ref={el => { if (el) el.indeterminate = !!someChecked; }}
                  onChange={onToggleAll}
                  className="h-4 w-4 accent-[#685B72]"
                  data-testid="up-bulk-select-all"
                />
              </th>
            )}
            <th className="py-2 pr-3">Action</th>
            <th className="py-2 pr-3">Client</th>
            <th className="py-2 pr-3">DOS</th>
            <th className="py-2 pr-3">Code</th>
            <th className="py-2 pr-3">Payer</th>
            <th className="py-2 pr-3 text-right">Paid</th>
            <th className="py-2 pr-3 text-right">Rate</th>
            <th className="py-2 pr-3 text-right">Underpaid</th>
            <th className="py-2 pr-3">Aged</th>
            <th className="py-2 pr-3">Status</th>
            <th className="py-2 pr-3">Follow-up</th>
            <th className="py-2 pr-3">Owner</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const overdue = r.action_needed === 'Action' && r.status !== 'New';
            const isChecked = selectable && selected && selected.has(r.appt_key);
            return (
              <tr
                key={r.appt_key}
                onClick={() => onOpen(r)}
                className={`border-b cursor-pointer ${isChecked ? 'bg-[#F1EDF5]' : 'hover:bg-[#F1EDF5]/40'}`}
                data-testid={`up-row-${r.appt_key}`}
              >
                {selectable && (
                  <td className="py-2 pr-3" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${r.client} ${fmtDate(r.dos)}`}
                      checked={!!isChecked}
                      onChange={() => onToggleRow(r.appt_key)}
                      className="h-4 w-4 accent-[#685B72]"
                      data-testid={`up-bulk-select-${r.appt_key}`}
                    />
                  </td>
                )}
                <td className="py-2 pr-3">
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                      r.action_needed === 'Action'
                        ? 'bg-[#F5EAEA] text-[#8A3A3A]'
                        : 'bg-[#E4EEE9] text-[#3B6F5D]'
                    }`}
                    title={overdue ? 'Follow-up date has arrived — please act' : ''}
                  >
                    {r.action_needed === 'Action' ? '⚠ Action' : '✓ Scheduled'}
                  </span>
                </td>
                <td className="py-2 pr-3 font-medium text-[#2D2833]">{r.client}</td>
                <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{fmtDate(r.dos)}</td>
                <td className="py-2 pr-3 font-mono text-xs">{r.code}</td>
                <td className="py-2 pr-3 text-slate-600">{r.raw_payer}<div className="text-[10px] text-slate-400">{r.payer_group}</div></td>
                <td className="py-2 pr-3 text-right tabular-nums text-slate-600">{money(r.total_paid)}</td>
                <td className="py-2 pr-3 text-right tabular-nums text-slate-600">{money(r.contract_rate)}</td>
                <td className="py-2 pr-3 text-right tabular-nums font-semibold text-[#8A3A3A]">{money(r.underpaid)}</td>
                <td className="py-2 pr-3">
                  <span className={`text-[11px] ${r.days_aged >= 91 ? 'text-[#8A3A3A] font-semibold' : r.days_aged >= 61 ? 'text-[#B08A3A]' : 'text-slate-500'}`}>
                    {r.days_aged}d
                  </span>
                </td>
                <td className="py-2 pr-3 text-slate-600">{r.status}</td>
                <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{r.next_follow_up ? fmtDate(r.next_follow_up) : '—'}</td>
                <td className="py-2 pr-3 text-slate-600">{r.owner || '—'}</td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={selectable ? 13 : 12} className="py-6 text-center text-slate-400">Nothing here.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Select({ value, onChange, options, labels = {}, placeholder = '', tid }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="h-9 text-sm rounded-md border border-[#E8E5EC] px-2 bg-white"
      data-testid={tid}
    >
      {options.map(o => (
        <option key={o} value={o}>{o === '' ? (placeholder || 'Any') : (labels[o] || o)}</option>
      ))}
    </select>
  );
}

function RowEditor({ row, config, onClose, onSaved }) {
  const [form, setForm] = useState({
    status: row.status || 'New',
    disposition: row.disposition || '',
    notes: row.notes || '',
    ref_claim: row.ref_claim || '',
    owner: row.owner || '',
    next_follow_up: row.next_follow_up ? row.next_follow_up.slice(0,10) : '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (v) => setForm(prev => ({ ...prev, [k]: typeof v === 'object' ? v.target.value : v }));

  const save = async () => {
    // Iter 261 — status enforcement. If the admin filled a disposition,
    // an owner, or a next-follow-up date the row is no longer "New" and
    // the status MUST reflect that. We refuse to save silently in that
    // combination and prompt them to advance the status themselves —
    // no auto-mutation of the dropdown value.
    if (form.status === 'New' && (form.disposition || form.next_follow_up || form.owner)) {
      toast.error(
        'Update the status before saving — a disposition, follow-up, or owner is set. Pick a status other than "New" (e.g. Under Review, Called Insurance, Resubmitted).',
      );
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/underpayment/tracker/${encodeURIComponent(row.appt_key)}`, form);
      toast.success('Saved');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="up-editor">
        <DialogHeader>
          <DialogTitle>{row.client} · {fmtDate(row.dos)} · {row.code}</DialogTitle>
          <DialogDescription>
            {row.raw_payer} · {row.payer_group} · Underpaid <b className="text-[#8A3A3A]">{money(row.underpaid)}</b> · Aged {row.days_aged} days
          </DialogDescription>
        </DialogHeader>
        {/* Read-only system field summary */}
        <div className="grid grid-cols-4 gap-3 text-xs mt-2 mb-4 bg-[#F1EDF5]/40 border border-[#E8E5EC] rounded-lg p-3">
          <div><div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Total Paid</div><div className="font-semibold text-[#2D2833]">{money(row.total_paid)}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Contract Rate</div><div className="font-semibold text-[#2D2833]">{money(row.contract_rate)}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Shortfall</div><div className="font-semibold text-[#8A3A3A]">{money(row.underpaid)}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Days Aged</div><div className="font-semibold text-[#2D2833]">{row.days_aged}</div></div>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <select value={form.status} onChange={set('status')} className="h-9 w-full rounded-md border border-[#E8E5EC] px-2 bg-white text-sm" data-testid="up-editor-status">
                {(config?.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Disposition">
              <select value={form.disposition} onChange={set('disposition')} className="h-9 w-full rounded-md border border-[#E8E5EC] px-2 bg-white text-sm" data-testid="up-editor-disposition">
                <option value="">—</option>
                {(config?.dispositions || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ref / Claim #">
              <Input value={form.ref_claim} onChange={set('ref_claim')} data-testid="up-editor-ref" />
            </Field>
            <Field label="Owner (assign to)">
              <Input value={form.owner} onChange={set('owner')} placeholder="e.g. Varnoor" data-testid="up-editor-owner" />
            </Field>
          </div>
          <Field label="Next Follow-up">
            <Input type="date" value={form.next_follow_up} onChange={set('next_follow_up')} data-testid="up-editor-followup" />
          </Field>
          <Field label="Notes">
            <Textarea rows={4} value={form.notes} onChange={set('notes')} placeholder="What did you do, what's the plan, who owes you a callback?" data-testid="up-editor-notes" />
          </Field>
          {row.last_action_date && (
            <div className="text-[11px] text-slate-500">Last touched {fmtDate(row.last_action_date)}{row.updated_by ? ` by ${row.updated_by}` : ''}.</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="up-editor-cancel"><X size={12} className="mr-1" /> Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#685B72] hover:bg-[#4A3B5F] text-white" data-testid="up-editor-save">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] font-semibold uppercase tracking-widest text-[#685B72]">{label}</label>
      {children}
    </div>
  );
}

// ═══════════ Bulk editor ═══════════
// One dialog, one submit → applies the same admin-owned patch to every
// selected appointment via /underpayment/tracker/bulk-update. Empty fields
// are omitted so the caller can pick exactly which columns to overwrite.
function BulkEditor({ count, apptKeys, config, onClose, onSaved }) {
  const [form, setForm] = useState({
    status: '',
    disposition: '',
    owner: '',
    next_follow_up: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) =>
    setForm(prev => ({ ...prev, [k]: typeof e === 'object' ? e.target.value : e }));

  const save = async () => {
    // Build a sparse updates payload — only include fields the user set.
    const updates = {};
    if (form.status)         updates.status         = form.status;
    if (form.disposition)    updates.disposition    = form.disposition;
    if (form.owner)          updates.owner          = form.owner;
    if (form.next_follow_up) updates.next_follow_up = form.next_follow_up;
    if (form.notes)          updates.notes          = form.notes;
    if (!Object.keys(updates).length) {
      toast.error('Set at least one field to bulk-apply.');
      return;
    }
    // Iter 261 — status enforcement in bulk. Blocks the identical
    // "disposition/follow-up/owner set but status still New" pattern
    // that RowEditor blocks. Applies whenever the user explicitly picks
    // "New" (blank status is "leave unchanged" — no violation).
    if (form.status === 'New' && (form.disposition || form.next_follow_up || form.owner)) {
      toast.error(
        'Update the status before saving — a disposition, follow-up, or owner is set. Pick a status other than "New" (e.g. Under Review, Called Insurance, Resubmitted).',
      );
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/underpayment/tracker/bulk-update', {
        appt_keys: apptKeys,
        updates,
      });
      const missed = (data.not_found || []).length;
      toast.success(
        `Updated ${data.updated_count} appointment${data.updated_count === 1 ? '' : 's'}` +
        (missed ? ` · ${missed} skipped (not found)` : '')
      );
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="up-bulk-editor">
        <DialogHeader>
          <DialogTitle>Bulk edit {count} appointment{count === 1 ? '' : 's'}</DialogTitle>
          <DialogDescription>
            Any field you fill will be written to every selected row.
            Fields left blank are untouched. Notes overwrite the existing
            note for each row.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Status (optional)">
              <select
                value={form.status} onChange={set('status')}
                className="h-9 w-full rounded-md border border-[#E8E5EC] px-2 bg-white text-sm"
                data-testid="up-bulk-status"
              >
                <option value="">— leave unchanged —</option>
                {(config?.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Disposition (optional)">
              <select
                value={form.disposition} onChange={set('disposition')}
                className="h-9 w-full rounded-md border border-[#E8E5EC] px-2 bg-white text-sm"
                data-testid="up-bulk-disposition"
              >
                <option value="">— leave unchanged —</option>
                {(config?.dispositions || []).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Owner (optional)">
              <Input
                value={form.owner} onChange={set('owner')}
                placeholder="e.g. Varnoor"
                data-testid="up-bulk-owner"
              />
            </Field>
            <Field label="Next Follow-up (optional)">
              <Input
                type="date"
                value={form.next_follow_up} onChange={set('next_follow_up')}
                data-testid="up-bulk-followup"
              />
            </Field>
          </div>
          <Field label="Notes (optional — overwrites existing)">
            <Textarea
              rows={3}
              value={form.notes} onChange={set('notes')}
              placeholder="What decision explains this batch? e.g. 'Denied — timely filing. Written off per Amy 2/12.'"
              data-testid="up-bulk-notes"
            />
          </Field>
          <div className="text-[11px] text-[#685B72] bg-[#F1EDF5]/50 border border-[#E8E5EC] rounded p-2">
            {count} appointment{count === 1 ? '' : 's'} selected · terminal statuses
            (<b>Resolved</b>, <b>Written Off</b>) will archive to Terminal History on the next refresh.
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline" onClick={onClose}
            data-testid="up-bulk-cancel"
          >
            <X size={12} className="mr-1" /> Cancel
          </Button>
          <Button
            onClick={save} disabled={saving}
            className="bg-[#685B72] hover:bg-[#4A3B5F] text-white"
            data-testid="up-bulk-save"
          >
            {saving ? 'Applying…' : `Apply to ${count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════ Config Drawer ═══════════
function ConfigDrawer({ config, onClose, onSaved }) {
  // Local editable copies. Keep them local so a cancel discards everything.
  const [statuses, setStatuses] = useState(() => [...(config?.statuses || [])]);
  const [dispositions, setDispositions] = useState(() => [...(config?.dispositions || [])]);
  const [tolerance, setTolerance] = useState(() => String(config?.tolerance ?? 2));
  const [cadence, setCadence] = useState(() => String(config?.follow_up_days ?? 21));
  const [minDosAge, setMinDosAge] = useState(() => String(config?.min_dos_age_days ?? 30));
  const [rules, setRules] = useState(() =>
    (config?.payer_rules || []).map(r => (Array.isArray(r) ? { pattern: r[0], group: r[1] } : { pattern: r.pattern || '', group: r.group || '' }))
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    // Basic client-side validation so the API 400s aren't the first feedback.
    const cleanStatuses = statuses.map(s => s.trim()).filter(Boolean);
    const cleanDisp = dispositions.map(s => s.trim()).filter(Boolean);
    const tol = Number(tolerance);
    const cad = parseInt(cadence, 10);
    const minAge = parseInt(minDosAge, 10);
    if (!cleanStatuses.length) return toast.error('At least one status is required.');
    if (!cleanDisp.length) return toast.error('At least one disposition is required.');
    if (Number.isNaN(tol) || tol < 0) return toast.error('Tolerance must be a non-negative number.');
    if (Number.isNaN(cad) || cad < 1) return toast.error('Cadence must be a positive integer.');
    if (Number.isNaN(minAge) || minAge < 0 || minAge > 180) return toast.error('Grace window must be between 0 and 180 days.');
    const cleanRules = rules
      .map(r => ({ pattern: (r.pattern || '').trim().toLowerCase(), group: (r.group || '').trim() }))
      .filter(r => r.pattern && r.group);

    setSaving(true);
    try {
      const { data } = await api.put('/underpayment/config', {
        statuses: cleanStatuses,
        dispositions: cleanDisp,
        tolerance: tol,
        follow_up_days: cad,
        min_dos_age_days: minAge,
        payer_rules: cleanRules.map(r => [r.pattern, r.group]),
      });
      toast.success('Settings saved. Next refresh picks these up.');
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="up-config-drawer">
        <DialogHeader>
          <DialogTitle>Underpayment settings</DialogTitle>
          <DialogDescription>
            Edit the vocabulary + thresholds the tracker uses. Changes take effect on the next refresh — existing rows keep whatever status they had, but any new dropdown value you add here appears in the row editor immediately.
          </DialogDescription>
        </DialogHeader>

        {/* Thresholds */}
        <div className="grid grid-cols-2 gap-4 mt-2">
          <Field label="Tolerance ($)">
            <Input
              type="number" step="0.01" min="0"
              value={tolerance} onChange={e => setTolerance(e.target.value)}
              data-testid="up-config-tolerance"
            />
            <div className="text-[10px] text-[#685B72] mt-0.5">Shortfalls at or below this amount are ignored. Default $2.00.</div>
          </Field>
          <Field label="Follow-up cadence (days)">
            <Input
              type="number" min="1" step="1"
              value={cadence} onChange={e => setCadence(e.target.value)}
              data-testid="up-config-cadence"
            />
            <div className="text-[10px] text-[#685B72] mt-0.5">Auto-fills the "Next Follow-up" field when an admin changes status without setting a date.</div>
          </Field>
          <Field label="Claim-age grace window (days)">
            <Input
              type="number" min="0" max="180" step="1"
              value={minDosAge} onChange={e => setMinDosAge(e.target.value)}
              data-testid="up-config-min-dos-age"
            />
            <div className="text-[10px] text-[#685B72] mt-0.5">Appointments newer than this many days are skipped — payers need time to process the claim. Default 30 days. Takes effect on the next refresh.</div>
          </Field>
        </div>

        <ListEditor
          title="Statuses"
          hint="These populate the Status dropdown in the row editor. 'Resolved' and 'Written Off' are terminal — items with either status are archived to Terminal History on the next refresh."
          items={statuses}
          onChange={setStatuses}
          testId="up-config-statuses"
          protectedItems={['Resolved', 'Written Off']}
          placeholder="e.g. Escalated to Manager"
        />

        <ListEditor
          title="Dispositions"
          hint="Reason codes the admin picks alongside a status. Free to add / remove — no protected values."
          items={dispositions}
          onChange={setDispositions}
          testId="up-config-dispositions"
          protectedItems={[]}
          placeholder="e.g. Waiting on Provider"
        />

        <PayerRulesEditor rules={rules} onChange={setRules} />

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="up-config-cancel">Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#685B72] hover:bg-[#4A3B5F] text-white"
            data-testid="up-config-save"
          >
            {saving ? 'Saving…' : 'Save settings'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ListEditor({ title, hint, items, onChange, testId, protectedItems = [], placeholder = '' }) {
  const [draft, setDraft] = useState('');
  const addItem = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.some(x => x.trim().toLowerCase() === v.toLowerCase())) {
      toast.error(`"${v}" is already in the list.`);
      return;
    }
    onChange([...items, v]);
    setDraft('');
  };
  const remove = (idx) => {
    const v = items[idx];
    if (protectedItems.includes(v)) {
      toast.error(`"${v}" is required — the tracker uses it as a terminal state.`);
      return;
    }
    onChange(items.filter((_, i) => i !== idx));
  };
  const move = (idx, delta) => {
    const next = [...items];
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="mt-5" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest font-semibold text-[#685B72]">{title}</div>
      {hint && <div className="text-[11px] text-[#685B72] mt-0.5 mb-2 leading-snug">{hint}</div>}
      <div className="rounded-lg border border-[#E8E5EC] bg-white divide-y divide-[#F1EDF5]">
        {items.length === 0 && (
          <div className="p-3 text-xs text-slate-400 italic">Nothing here yet — add your first {title.toLowerCase().replace(/s$/, '')}.</div>
        )}
        {items.map((v, idx) => {
          const isProtected = protectedItems.includes(v);
          return (
            <div key={`${v}-${idx}`} className="flex items-center gap-1 px-3 py-1.5" data-testid={`${testId}-row-${idx}`}>
              <span className="flex-1 text-sm text-[#2D2833] truncate">
                {v}
                {isProtected && <span className="ml-2 text-[9px] uppercase tracking-widest text-[#B08A3A]">protected</span>}
              </span>
              <button
                type="button"
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                aria-label="Move up"
                data-testid={`${testId}-up-${idx}`}
              >
                <ChevronUp size={13} />
              </button>
              <button
                type="button"
                onClick={() => move(idx, 1)}
                disabled={idx === items.length - 1}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"
                aria-label="Move down"
                data-testid={`${testId}-down-${idx}`}
              >
                <ChevronDown size={13} />
              </button>
              <button
                type="button"
                onClick={() => remove(idx)}
                disabled={isProtected}
                className="p-1 rounded hover:bg-red-50 text-[#8A3A3A] disabled:opacity-30"
                aria-label="Remove"
                data-testid={`${testId}-remove-${idx}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex gap-2">
        <Input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
          placeholder={placeholder}
          className="h-9 text-sm"
          data-testid={`${testId}-input`}
        />
        <Button
          type="button"
          onClick={addItem}
          variant="outline"
          className="h-9 rounded-md"
          data-testid={`${testId}-add`}
        >
          <Plus size={12} className="mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

function PayerRulesEditor({ rules, onChange }) {
  const [pattern, setPattern] = useState('');
  const [group, setGroup] = useState('');

  const add = () => {
    const p = pattern.trim().toLowerCase();
    const g = group.trim();
    if (!p || !g) return toast.error('Both substring + group are required.');
    if (rules.some(r => (r.pattern || '').toLowerCase() === p)) {
      return toast.error(`"${p}" is already mapped.`);
    }
    onChange([...rules, { pattern: p, group: g }]);
    setPattern(''); setGroup('');
  };
  const remove = (idx) => onChange(rules.filter((_, i) => i !== idx));
  const move = (idx, delta) => {
    const next = [...rules];
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const editRule = (idx, key, value) => {
    onChange(rules.map((r, i) => i === idx ? { ...r, [key]: value } : r));
  };

  return (
    <div className="mt-5" data-testid="up-config-payer-rules">
      <div className="text-[11px] uppercase tracking-widest font-semibold text-[#685B72]">Payer normalization rules</div>
      <div className="text-[11px] text-[#685B72] mt-0.5 mb-2 leading-snug">
        Substring match on the raw payer name (case-insensitive), <b>first match wins</b>. Order matters — put more-specific patterns above general ones (e.g. <code>surest</code> before <code>united</code>). Unmatched raw payers fall back to <b>All Other</b>.
      </div>
      <div className="rounded-lg border border-[#E8E5EC] bg-white divide-y divide-[#F1EDF5] max-h-64 overflow-y-auto">
        {rules.length === 0 && (
          <div className="p-3 text-xs text-slate-400 italic">No rules yet — every raw payer will land in "All Other".</div>
        )}
        {rules.map((r, idx) => (
          <div key={idx} className="flex items-center gap-1 px-3 py-1.5" data-testid={`up-config-rule-${idx}`}>
            <span className="text-[10px] font-mono text-slate-400 w-6 text-right">{idx + 1}</span>
            <Input
              value={r.pattern}
              onChange={e => editRule(idx, 'pattern', e.target.value.toLowerCase())}
              className="h-7 text-xs font-mono flex-1"
              placeholder="substring"
              data-testid={`up-config-rule-pattern-${idx}`}
            />
            <span className="text-slate-300 text-xs">→</span>
            <Input
              value={r.group}
              onChange={e => editRule(idx, 'group', e.target.value)}
              className="h-7 text-xs flex-1"
              placeholder="Payer Group"
              data-testid={`up-config-rule-group-${idx}`}
            />
            <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0}
                    className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" aria-label="Move up"
                    data-testid={`up-config-rule-up-${idx}`}>
              <ChevronUp size={13} />
            </button>
            <button type="button" onClick={() => move(idx, 1)} disabled={idx === rules.length - 1}
                    className="p-1 rounded hover:bg-slate-100 disabled:opacity-30" aria-label="Move down"
                    data-testid={`up-config-rule-down-${idx}`}>
              <ChevronDown size={13} />
            </button>
            <button type="button" onClick={() => remove(idx)}
                    className="p-1 rounded hover:bg-red-50 text-[#8A3A3A]" aria-label="Remove"
                    data-testid={`up-config-rule-remove-${idx}`}>
              <Trash2 size={13} />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <Input
          value={pattern}
          onChange={e => setPattern(e.target.value.toLowerCase())}
          placeholder="Substring (e.g. optum)"
          className="h-9 text-sm font-mono flex-1"
          data-testid="up-config-rule-new-pattern"
        />
        <Input
          value={group}
          onChange={e => setGroup(e.target.value)}
          placeholder="Payer Group (e.g. UHC)"
          className="h-9 text-sm flex-1"
          data-testid="up-config-rule-new-group"
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <Button type="button" onClick={add} variant="outline" className="h-9 rounded-md" data-testid="up-config-rule-add">
          <Plus size={12} className="mr-1" /> Add rule
        </Button>
      </div>
    </div>
  );
}
